import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from "reactstrap";
import { withRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { withTranslation } from "react-i18next";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { useUpdateVersionPolicy, useVersionPolicy } from "../../hooks/useAppConfig";

/*
 * One card per store. The thresholds are per platform on purpose: iOS and
 * Android never publish the same day, and a single number would lock the
 * users of one store out while the other build is still in review.
 */
const PLATFORMS = [
  { key: "ios", label: "iOS (App Store)", storeHint: "https://apps.apple.com/app/id…" },
  {
    key: "android",
    label: "Android (Google Play)",
    storeHint: "https://play.google.com/store/apps/details?id=…",
  },
];

const EMPTY = { min_version: "", latest_version: "", store_url: "" };

const PlatformCard = ({ platform, initial, onSave, saving, t }) => {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    setForm({
      min_version: initial?.min_version ?? "",
      latest_version: initial?.latest_version ?? "",
      store_url: initial?.store_url ?? "",
    });
  }, [initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = () =>
    onSave({
      platform: platform.key,
      min_version: form.min_version.trim(),
      latest_version: form.latest_version.trim() || null,
      store_url: form.store_url.trim() || null,
    });

  return (
    <Card>
      <CardHeader>
        <h5 className="card-title mb-0">{platform.label}</h5>
      </CardHeader>
      <CardBody>
        <div className="mb-3">
          <Label htmlFor={`${platform.key}-min`}>{t("Version minimale requise")}</Label>
          <Input
            id={`${platform.key}-min`}
            value={form.min_version}
            onChange={set("min_version")}
            placeholder="1.4.0"
          />
          <p className="text-muted fs-12 mb-0">
            {t("En dessous de cette version, l'application est bloquée jusqu'à la mise à jour.")}
          </p>
        </div>
        <div className="mb-3">
          <Label htmlFor={`${platform.key}-latest`}>{t("Dernière version publiée")}</Label>
          <Input
            id={`${platform.key}-latest`}
            value={form.latest_version}
            onChange={set("latest_version")}
            placeholder="1.5.0"
          />
          <p className="text-muted fs-12 mb-0">
            {t("Entre la version minimale et celle-ci, l'application invite à mettre à jour sans bloquer.")}
          </p>
        </div>
        <div className="mb-3">
          <Label htmlFor={`${platform.key}-store`}>{t("Lien du store")}</Label>
          <Input
            id={`${platform.key}-store`}
            value={form.store_url}
            onChange={set("store_url")}
            placeholder={platform.storeHint}
          />
        </div>
        <Button color="warning" onClick={submit} disabled={saving || !form.min_version.trim()}>
          {saving ? t("Enregistrement…") : t("Enregistrer")}
        </Button>
      </CardBody>
    </Card>
  );
};

PlatformCard.propTypes = {
  platform: PropTypes.object,
  initial: PropTypes.object,
  onSave: PropTypes.func,
  saving: PropTypes.bool,
  t: PropTypes.func,
};

const AppVersionPolicy = (props) => {
  const policyQ = useVersionPolicy();
  const updateMutation = useUpdateVersionPolicy();

  document.title = props.t("Version de l'application");

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={props.t("Version de l'application")} pageTitle={props.t("Paramètres")} />
        <p className="text-muted">
          {props.t("Chaque store publie à son rythme : les seuils se règlent par plateforme.")}
        </p>
        <Row>
          {PLATFORMS.map((platform) => (
            <Col xl={6} key={platform.key}>
              <PlatformCard
                platform={platform}
                initial={policyQ.data?.[platform.key]}
                onSave={(payload) => updateMutation.mutate(payload)}
                saving={updateMutation.isPending}
                t={props.t}
              />
            </Col>
          ))}
        </Row>
        <ToastContainer closeButton={false} />
      </Container>
    </div>
  );
};

AppVersionPolicy.propTypes = { t: PropTypes.any };

export default withRouter(withTranslation()(AppVersionPolicy));
